import { QueryClient } from '@tanstack/react-query';
import { logger } from '@/utils/logger';

/**
 * THROTTLE leading-edge para invalidações em rajada.
 *
 * Estratégia: executa IMEDIATAMENTE no primeiro evento (zero delay percebido)
 * e agrupa subsequentes numa janela de DEBOUNCE_MS — se chegou ≥1 evento
 * durante a janela, dispara uma segunda execução ao fim. Isso garante:
 *
 * 1. UX rápida: 1º evento dispara invalidate imediato (UI atualiza em ms)
 * 2. Anti-rajada: bursts subsequentes do GC sync (200+ rows em 10s) ficam
 *    agrupados em 1 invalidate adicional por janela, não N.
 *
 * Antes era trailing-edge (sempre esperava 400ms antes de executar). Walter
 * percebia delay maior na Logística que no Comercial — fix 2026-05-16.
 */
// Walter 2026-05-16: 400ms causou fan-out massivo em prod (24 sockets × 7
// UPDATEs/s ≈ 60 refetches/s × 750KB por refetch ≈ 45MB/s). Aumentado
// para 2000ms para reduzir 5× a taxa de refetch. UX perde 1.6s de
// latência mas server respira.
const DEBOUNCE_MS = 2000;
const pendingTimers = new Map<string, ReturnType<typeof setTimeout>>();
const pendingFollowup = new Map<string, boolean>();
const pendingImmediate = new Map<string, boolean>();

function debounceCall(
  key: string,
  immediate: boolean,
  fn: (immediate: boolean) => void,
): void {
  if (pendingTimers.has(key)) {
    // Janela ativa — marca que houve follow-up (será disparado ao fim)
    if (immediate) pendingFollowup.set(key, true);
    pendingFollowup.set(key, pendingFollowup.get(key) ?? true);
    if (immediate) pendingImmediate.set(key, true);
    return;
  }

  // Primeiro evento da janela: executa imediato
  fn(immediate);

  // Agenda janela pra agrupar follow-ups
  if (immediate) pendingImmediate.set(key, true);
  const timer = setTimeout(() => {
    const hadFollowup = pendingFollowup.get(key) === true;
    const wasImmediate = pendingImmediate.get(key) === true;
    pendingFollowup.delete(key);
    pendingImmediate.delete(key);
    pendingTimers.delete(key);
    if (hadFollowup) fn(wasImmediate);
  }, DEBOUNCE_MS);
  pendingTimers.set(key, timer);
}

/**
 * PADRÃO CENTRALIZADO DE INVALIDAÇÃO
 * Sprint 3 - Fase 10: Elimina redundância em 5 arquivos
 * 
 * Uso: sempre que modificar production_steps ou orders
 */

interface InvalidateProductionOptions {
  /**
   * Incluir invalidação específica de order_steps (modal de detalhes)
   * Passar order_id se disponível, ou true para invalidar todos
   */
  orderSteps?: string | true;
  
  /**
   * Incluir invalidação de operator-tasks (painel do operador)
   */
  operatorTasks?: boolean;
  
  /**
   * Forçar refetch imediato das queries ativas (não apenas marcar como stale)
   */
  immediate?: boolean;
}

/**
 * Invalida TODAS as queries relacionadas a production steps
 * Usa refetchType: 'all' para capturar queries parametrizadas
 */
export const invalidateProductionQueries = (
  queryClient: QueryClient,
  options: InvalidateProductionOptions = {}
) => {
  // 1. Orders (sempre)
  queryClient.invalidateQueries({ queryKey: ['orders'] });
  queryClient.invalidateQueries({ queryKey: ['orders-map'] });
  
  // 2. Production Steps (com refetchType: 'all' para capturar variações)
  queryClient.invalidateQueries({
    queryKey: ['production-steps'],
    refetchType: 'all' // ← CRÍTICO: invalida ['production-steps', 'SETOR']
  });
  queryClient.invalidateQueries({ queryKey: ['all-production-steps'] });

  // 2.5. Painel "Chão de fábrica" (Gerente): mutation de step (Iniciar/Pausar/Concluir)
  // invalida ['painel'] — só invalidar, nunca refetch forçado (CLAUDE.md §7.5).
  queryClient.invalidateQueries({ queryKey: ['painel'] });
  
  // 3. Order Steps (modal de detalhes)
  if (options.orderSteps === true) {
    // Invalidar todos os order_steps
    queryClient.invalidateQueries({ 
      queryKey: ['order_steps'],
      refetchType: 'all'
    });
  } else if (typeof options.orderSteps === 'string') {
    // Invalidar order_steps específico
    queryClient.invalidateQueries({ 
      queryKey: ['order_steps', options.orderSteps] 
    });
  }
  
  // (2026-10-08, auditoria A14) `operatorTasks` deixou de invalidar
  // ['operator-tasks']: nenhuma consulta usa essa chave. A opção fica aceita
  // para não mexer nos 10 chamadores; o painel do operador lê
  // 'all-production-steps', já coberto acima.

  // 5. Forçar refetch imediato das queries ativas (dashboard do gerente)
  if (options.immediate) {
    queryClient.refetchQueries({
      queryKey: ['all-production-steps'],
      type: 'active'
    });
    queryClient.refetchQueries({
      queryKey: ['production-steps'],
      type: 'active'
    });
    queryClient.refetchQueries({
      queryKey: ['orders-map'],
      type: 'active'
    });
  }
};

/**
 * Versão debounced — usar em listeners de realtime para evitar refetch em
 * rajada quando o backend faz UPDATEs em massa (ex: GC sync polling).
 */
export const debouncedInvalidateProductionQueries = (
  queryClient: QueryClient,
  options: InvalidateProductionOptions = {}
) => {
  debounceCall('production', options.immediate === true, (immediate) => {
    invalidateProductionQueries(queryClient, { ...options, immediate });
  });
};

interface InvalidateLogisticsOptions {
  /**
   * Forçar refetch imediato (não apenas marcar como stale)
   */
  immediate?: boolean;
}

/**
 * Invalida TODAS as queries relacionadas a logistics_orders
 * Usa refetchType: 'all' para capturar queries parametrizadas
 * Ex: ['logistics-orders', true] e ['logistics-orders', false]
 */
export const invalidateLogisticsQueries = (
  queryClient: QueryClient,
  options: InvalidateLogisticsOptions = {}
) => {
  logger.debug('🔄 [queryInvalidation] Invalidando logistics queries', options);

  // Invalidar com refetchType: 'all' para capturar TODAS as variações
  queryClient.invalidateQueries({
    queryKey: ['logistics-orders'],
    refetchType: 'all'
  });

  // Se immediate: true, forçar refetch de TODAS as queries
  if (options.immediate) {
    queryClient.refetchQueries({
      queryKey: ['logistics-orders'],
      type: 'all'
    });
  }
};

/**
 * Versão debounced — usar em listeners de realtime para evitar refetch em
 * rajada quando o backend faz UPDATEs em massa (ex: GC sync polling).
 */
export const debouncedInvalidateLogisticsQueries = (
  queryClient: QueryClient,
  options: InvalidateLogisticsOptions = {}
) => {
  debounceCall('logistics', options.immediate === true, (immediate) => {
    invalidateLogisticsQueries(queryClient, { immediate });
  });
};

// ============= COMMERCE/ARTE QUERIES =============

interface InvalidateCommerceOptions {
  immediate?: boolean;
}

export const invalidateCommerceQueries = (
  queryClient: QueryClient,
  options: InvalidateCommerceOptions = {}
) => {
  logger.debug('🔄 [queryInvalidation] Invalidando commerce queries', options);

  // Arte Jobs
  queryClient.invalidateQueries({ queryKey: ['arte-jobs'] });
  queryClient.invalidateQueries({ queryKey: ['arte-job-delivery'] });

  // Walter 2026-05-17 part 2: restaurado refetchQueries forçado quando
  // immediate=true. Removi no commit 3e80a48 (perf CPU 103%→6.83%), mas
  // sem isso o realtime ficou inconsistente em queries com staleTime
  // longo. invalidateQueries SOZINHO marca stale + auto-refetcha active,
  // MAS apenas quando o observer está montado E React Query agenda
  // refetch (pode atrasar até 1 tick). Para realtime confiável, força
  // refetch agora. Tráfego controlado via DEBOUNCE_MS 2000ms (5× menos
  // chamadas que original 400ms) + pendência: reduzir payload arte_jobs.
  if (options.immediate) {
    queryClient.refetchQueries({ queryKey: ['arte-jobs'], type: 'active' });
  }
};

/**
 * Versão debounced — usar em listeners de realtime para evitar refetch em
 * rajada quando o backend faz UPDATEs em massa (ex: GC sync polling).
 */
export const debouncedInvalidateCommerceQueries = (
  queryClient: QueryClient,
  options: InvalidateCommerceOptions = {}
) => {
  debounceCall('commerce', options.immediate === true, (immediate) => {
    invalidateCommerceQueries(queryClient, { immediate });
  });
};

// ============= CONFIG/INTELLIGENCE QUERIES =============

interface InvalidateConfigOptions {
  metadataOrderId?: string;
  immediate?: boolean;
}

export const invalidateConfigQueries = (
  queryClient: QueryClient,
  options: InvalidateConfigOptions = {}
) => {
  logger.debug('🔄 [queryInvalidation] Invalidando config queries', options);
  
  // Intelligence Mode
  queryClient.invalidateQueries({ queryKey: ['intelligence-mode'] });
  
  // Order Metadata
  if (options.metadataOrderId) {
    queryClient.invalidateQueries({ 
      queryKey: ['order-metadata', options.metadataOrderId] 
    });
  }
  queryClient.invalidateQueries({ 
    queryKey: ['order-metadata'],
    refetchType: 'active'
  });
  // O mapa por pedido (bloco Materiais da programação, itens no card do
  // corte) lê `order_metadata_items`, que a reprojeção recria: sem invalidar
  // aqui, o gerente programava com os itens de ANTES da edição do vendedor.
  queryClient.invalidateQueries({ queryKey: ['order-metadata-map'] });
  
  if (options.immediate) {
    queryClient.refetchQueries({ queryKey: ['intelligence-mode'], type: 'active' });
  }
};
